import { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Space, Upload, Tooltip, message, Modal, Empty, Alert, Tag } from 'antd';
import { 
    PlayCircleOutlined, 
    PauseCircleOutlined, 
    UploadOutlined, 
    ScissorOutlined,
    DeleteOutlined,
    ClearOutlined,
    EditOutlined,
    DownloadOutlined,
    WarningOutlined,
    HomeOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import stores from '@stores';
import useSetImg from '@hooks/useSetImg';
import { ConfigProvider, theme } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';
import VideoSnapshot from '@utils/VideoSnapshot';
import SEO from '@components/seo/SEO';

// 使用sessionStorage存储当前编辑的图片数据
const EDIT_IMAGE_KEY = 'currentEditImage';
// 已编辑截图的ID集合
const EDITED_SNAPSHOTS_KEY = 'editedSnapshots';

// 使用常量限制保存到localStorage的视频截图数量
const MAX_SNAPSHOTS_IN_STORAGE = 20;

const VideoCapture = observer(() => {
    const [messageApi, contextHolder] = message.useMessage();
    const getFile = useSetImg(stores);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const videoRef = useRef(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [currentEditSnapshot, setCurrentEditSnapshot] = useState(null);
    const [videoError, setVideoError] = useState(null);
    const navigate = useNavigate();
    const videoSnapshotRef = useRef(null);
    const [editedSnapshotIds, setEditedSnapshotIds] = useState([]);
    
    // 设置视频元素引用
    useEffect(() => {
        if (videoRef.current) {
            stores.videoCapture.setVideoElement(videoRef.current);
        }
    }, [videoRef.current, videoLoaded]);
    
    // 在组件卸载前清理资源
    useEffect(() => {
        return () => {
            if (videoSnapshotRef.current) {
                videoSnapshotRef.current.destroy();
                videoSnapshotRef.current = null;
            }
        };
    }, []);
    
    // 加载已编辑过的截图列表
    useEffect(() => {
        try {
            const editedIds = localStorage.getItem(EDITED_SNAPSHOTS_KEY);
            if (editedIds) {
                const parsedIds = JSON.parse(editedIds);
                setEditedSnapshotIds(parsedIds);
                console.log('已加载编辑过的截图ID列表:', parsedIds);
            }
        } catch (err) {
            console.error('加载已编辑截图记录失败:', err);
        }
    }, []);
    
    // 如果是从编辑器返回，检查URL参数
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const fromEditor = urlParams.get('from') === 'editor';
        if (fromEditor) {
            // 检查是否有编辑中的截图ID
            const editedId = sessionStorage.getItem('lastEditedSnapshotId');
            if (editedId) {
                // 添加到已编辑列表，不依赖于state值，直接从localStorage读取当前值
                try {
                    let currentEditedIds = [];
                    const storedIds = localStorage.getItem(EDITED_SNAPSHOTS_KEY);
                    if (storedIds) {
                        currentEditedIds = JSON.parse(storedIds);
                    }
                    
                    // 只有当ID不存在于列表中时才添加
                    if (!currentEditedIds.includes(editedId)) {
                        currentEditedIds.push(editedId);
                        localStorage.setItem(EDITED_SNAPSHOTS_KEY, JSON.stringify(currentEditedIds));
                        // 更新状态
                        setEditedSnapshotIds(currentEditedIds);
                    }
                } catch (err) {
                    console.error('处理已编辑截图ID出错:', err);
                }
                
                // 清除会话存储中的ID
                sessionStorage.removeItem('lastEditedSnapshotId');
            }
            
            const url = new URL(window.location);
            url.searchParams.delete('from');
            window.history.replaceState({}, '', url);
        }
    }, []);
    
    // 返回主页
    const handleBackToHome = () => {
        navigate('/');
    };
    
    // 文件上传前检查
    const beforeUpload = (file) => {
        const isVideo = file.type.startsWith('video/');
        if (!isVideo) {
            messageApi.error('请上传视频文件！');
            return false;
        }
        
        // 检查文件大小 (限制为10GB)
        const isLt10G = file.size / 1024 / 1024 / 1024 < 10;
        if (!isLt10G) {
            messageApi.error('视频文件不能超过10GB！');
            return false;
        }
        
        // 清除之前的错误状态
        setVideoError(null);
        
        // 清理旧的VideoSnapshot实例
        if (videoSnapshotRef.current) {
            videoSnapshotRef.current.destroy();
        }
        
        // 创建新的VideoSnapshot实例
        try {
            videoSnapshotRef.current = new VideoSnapshot(file);
            
            // 加载视频
            stores.videoCapture.setVideoFile(file);
        } catch (err) {
            console.error("视频加载错误:", err);
            setVideoError("视频加载失败，可能是由于浏览器安全策略限制。");
        }
        
        return false; // 阻止默认上传行为
    };
    
    // 处理视频加载完成
    const handleVideoLoaded = () => {
        setVideoLoaded(true);
        setVideoError(null);
        messageApi.success('视频加载成功！');
    };
    
    // 处理视频加载错误
    const handleVideoError = (e) => {
        console.error("视频加载错误:", e);
        setVideoError("视频加载失败，可能是由于浏览器安全策略限制。请确保在网站安全策略中允许加载视频媒体。");
        setVideoLoaded(false);
    };
    
    // 捕获视频截图
    const captureSnapshot = async () => {
        if (!videoLoaded || videoError) {
            messageApi.warning('请先上传视频并确保它能正常播放！');
            return;
        }
        
        try {
            // 使用VideoSnapshot工具类获取当前时间的截图
            if (videoSnapshotRef.current) {
                const currentTime = videoRef.current.currentTime;
                const snapshotData = await videoSnapshotRef.current.takeSnapshot(currentTime, 'image/png', 1.0);
                
                // 直接使用原始图像数据，不进行压缩
                const snapshot = {
                    id: Date.now().toString(), // 使用时间戳作为ID
                    dataUrl: snapshotData.dataUrl,
                    timestamp: snapshotData.timestamp,
                    width: snapshotData.width,
                    height: snapshotData.height, 
                    createdAt: new Date().toISOString()
                };
                
                // 添加到MobX store，这里变成了异步操作
                try {
                    await stores.videoCapture.addSnapshot(snapshot);
                    messageApi.success('截图成功！');
                } catch (storeErr) {
                    console.error("保存截图到Store出错:", storeErr);
                    throw new Error("无法保存截图，请重试");
                }
            } else {
                throw new Error("视频处理工具未初始化");
            }
        } catch (err) {
            console.error("截图错误:", err);
            messageApi.error(`截图失败: ${err.message || '请重试'}`);
        }
    };
    
    // 下载截图
    const downloadSnapshot = (snapshot) => {
        const link = document.createElement('a');
        link.href = snapshot.dataUrl;
        link.download = `视频截图_${new Date().getTime()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    // 删除截图
    const deleteSnapshot = (id) => {
        Modal.confirm({
            title: '删除确认',
            content: '确定要删除这张截图吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    // 异步删除
                    if (await stores.videoCapture.deleteSnapshot(id)) {
                        // 从已编辑列表中也删除该ID
                        const newEditedIds = editedSnapshotIds.filter(editedId => editedId !== id);
                        setEditedSnapshotIds(newEditedIds);
                        localStorage.setItem(EDITED_SNAPSHOTS_KEY, JSON.stringify(newEditedIds));
                        
                        messageApi.success('截图已删除');
                    }
                } catch (err) {
                    console.error("删除截图失败:", err);
                    messageApi.error(`删除失败: ${err.message || '请重试'}`);
                }
            }
        });
    };
    
    // 清空所有截图
    const clearAllSnapshots = () => {
        if (stores.videoCapture.snapshots.length === 0) {
            messageApi.info('没有截图可清空');
            return;
        }
        
        Modal.confirm({
            title: '清空确认',
            content: '确定要清空所有截图吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    // 异步清空
                    await stores.videoCapture.clearSnapshots();
                    // 清空已编辑记录
                    setEditedSnapshotIds([]);
                    localStorage.removeItem(EDITED_SNAPSHOTS_KEY);
                    
                    messageApi.success('所有截图已清空');
                } catch (err) {
                    console.error("清空截图失败:", err);
                    messageApi.error(`清空失败: ${err.message || '请重试'}`);
                }
            }
        });
    };
    
    // 编辑截图 - 将截图发送到编辑器
    const editSnapshot = async (snapshot) => {
        try {
            console.log('开始编辑截图:', snapshot.id);
            
            try {
                // 方案1：使用 sessionStorage 传递图片数据（最简单的方式）
                sessionStorage.setItem(EDIT_IMAGE_KEY, snapshot.dataUrl);
                sessionStorage.setItem('lastEditedSnapshotId', snapshot.id);
                
                // 关闭编辑确认对话框
                setEditModalVisible(false);
                
                // 导航到主页并带上编辑参数
                navigate('/?editor=true&source=videoCapture');
            } catch (storageErr) {
                console.warn('通过sessionStorage传递图像数据失败，尝试备选方案:', storageErr);
                
                // 方案2：通过URL参数传递截图ID，编辑器直接从IndexedDB读取
                // 截图数据已经存储在IndexedDB中，所以无需额外保存
                // 只需要传递ID给编辑器即可
                sessionStorage.setItem('lastEditedSnapshotId', snapshot.id);
                setEditModalVisible(false);
                
                // 导航到编辑器，带上截图ID参数
                navigate(`/?editor=true&source=videoCapture&snapshotId=${snapshot.id}`);
                
                // 告知用户我们采用了备选方案
                messageApi.info('使用备选方式编辑大图片');
            }
        } catch (err) {
            console.error('准备编辑图片失败:', err);
            messageApi.error('无法准备编辑，请重试');
        }
    };
    
    // 进入编辑模式前的确认
    const confirmEdit = (snapshot) => {
        setCurrentEditSnapshot(snapshot);
        setEditModalVisible(true);
    };
    
    // 检查截图是否已编辑过
    const isSnapshotEdited = (snapshotId) => {
        // 首先检查状态中的编辑ID
        if (editedSnapshotIds.includes(snapshotId)) {
            return true;
        }
        
        // 也直接检查localStorage中的编辑ID，以防状态未同步
        try {
            const storedIds = localStorage.getItem(EDITED_SNAPSHOTS_KEY);
            if (storedIds) {
                const parsedIds = JSON.parse(storedIds);
                return parsedIds.includes(snapshotId);
            }
        } catch (err) {
            console.error('读取已编辑ID出错:', err);
        }
        
        return false;
    };
    
    return (
        <StyleProvider>
            <ConfigProvider
                theme={{
                    algorithm: stores.editor.isDark ? theme.darkAlgorithm : theme.defaultAlgorithm
                }}
            >
                <SEO 
                    title="视频截图工具 - 图美啦 | 一键捕获视频精彩瞬间"
                    description="图美啦视频截图工具，让您轻松从视频中捕获高质量截图，支持编辑、美化和导出，满足教程制作、游戏分享和产品演示等各种需求。"
                    keywords="视频截图,视频编辑,视频帧捕获,视频画面提取,视频教程制作,游戏截图,截图编辑,在线视频工具"
                    canonicalUrl="https://snapbeautify.toolooz.com/video-capture"
                />
                {contextHolder}
                <div className="flex flex-col w-full min-h-[80vh] p-4 gap-6 bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                    <div className="flex justify-between items-center">
                        <div className="text-xl font-bold text-center text-gray-800 dark:text-white">
                            视频截图工具
                        </div>
                        <Button 
                            type="primary" 
                            icon={<HomeOutlined />} 
                            onClick={handleBackToHome}
                        >
                            返回首页
                        </Button>
                    </div>
                    
                    {/* 从编辑器返回提示 */}
                    {new URLSearchParams(window.location.search).get('from') === 'editor' && (
                        <Alert
                            message="编辑完成提示"
                            description="您已从图片编辑器返回。如果需要继续编辑其他截图，请点击截图列表中的编辑按钮。"
                            type="success"
                            showIcon
                            closable
                        />
                    )}
                    
                    {/* 安全策略提示 */}
                    {videoError && (
                        <Alert
                            message="视频加载提示"
                            description={
                                <div>
                                    <p>{videoError}</p>
                                    <p>如果您在使用 Cloudflare Pages 部署，请确保在 _headers 文件中添加了适当的安全策略：</p>
                                    <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs overflow-auto">
                                        {`/*
  Content-Security-Policy: ... media-src 'self' blob:;`}
                                    </pre>
                                </div>
                            }
                            type="warning"
                            showIcon
                            icon={<WarningOutlined />}
                            closable
                        />
                    )}
                    
                    {/* 视频上传和播放区域 */}
                    <div className="flex flex-col items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                        {!stores.videoCapture.videoUrl ? (
                            <Upload.Dragger
                                name="video"
                                multiple={false}
                                showUploadList={false}
                                beforeUpload={beforeUpload}
                                className="w-full max-w-2xl"
                            >
                                <p className="ant-upload-drag-icon">
                                    <UploadOutlined />
                                </p>
                                <p className="ant-upload-text">点击或拖拽上传视频文件</p>
                                <p className="ant-upload-hint">
                                    支持常见视频格式，单个文件不超过10GB
                                </p>
                            </Upload.Dragger>
                        ) : (
                            <div className="flex flex-col w-full max-w-2xl gap-4">
                                {/* 视频播放器 */}
                                <div className="relative w-full rounded-lg overflow-hidden bg-black">
                                    <video
                                        ref={videoRef}
                                        src={stores.videoCapture.videoUrl}
                                        className="w-full"
                                        controls={true}
                                        onLoadedData={handleVideoLoaded}
                                        onError={handleVideoError}
                                        crossOrigin="anonymous"
                                    />
                                </div>
                                
                                {/* 视频控制区 */}
                                <div className="flex justify-between items-center">
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<ScissorOutlined />}
                                            onClick={captureSnapshot}
                                            disabled={!videoLoaded || !!videoError}
                                            loading={stores.videoCapture.isLoading}
                                        >
                                            截图
                                        </Button>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            支持高分辨率截图，保持原始质量
                                        </div>
                                    </Space>
                                    
                                    <Button
                                        type="default"
                                        onClick={() => {
                                            Modal.confirm({
                                                title: '更换视频',
                                                content: '更换视频将清空当前所有截图，确定要继续吗？',
                                                okText: '确定',
                                                cancelText: '取消',
                                                onOk: async () => {
                                                    try {
                                                        // 清理VideoSnapshot实例
                                                        if (videoSnapshotRef.current) {
                                                            videoSnapshotRef.current.destroy();
                                                            videoSnapshotRef.current = null;
                                                        }
                                                        
                                                        // 重置视频捕获状态(现在是异步的)
                                                        await stores.videoCapture.reset();
                                                        setVideoLoaded(false);
                                                        setVideoError(null);
                                                        
                                                        // 清空已编辑截图记录
                                                        setEditedSnapshotIds([]);
                                                        localStorage.removeItem(EDITED_SNAPSHOTS_KEY);
                                                        
                                                        messageApi.success('已清空视频和截图');
                                                    } catch (err) {
                                                        console.error('清空视频和截图失败:', err);
                                                        messageApi.error('清空失败，请重试');
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        更换视频
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* 截图展示区域 */}
                    <div className="flex flex-col gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                        <div className="flex justify-between items-center">
                            <div className="text-lg font-medium flex items-center">
                                截图列表 ({stores.videoCapture.snapshots.length})
                                {stores.videoCapture.isLoading && <span className="ml-2 text-sm text-gray-500">加载中...</span>}
                                <Tooltip title="使用 IndexedDB 存储，支持大尺寸图片和持久保存">
                                    <span className="ml-2 text-xs text-blue-500 cursor-pointer">
                                        <InfoCircleOutlined /> 本地存储
                                    </span>
                                </Tooltip>
                            </div>
                            {stores.videoCapture.snapshots.length > 0 && (
                                <Button 
                                    type="default" 
                                    danger 
                                    icon={<ClearOutlined />}
                                    onClick={clearAllSnapshots}
                                    loading={stores.videoCapture.isLoading}
                                >
                                    清空
                                </Button>
                            )}
                        </div>
                        
                        {stores.videoCapture.isLoading ? (
                            <div className="flex justify-center items-center h-40">
                                <div className="text-gray-500 text-center">
                                    <div className="mb-3">加载截图数据中...</div>
                                    <div className="ant-spin ant-spin-lg ant-spin-spinning">
                                        <span className="ant-spin-dot ant-spin-dot-spin">
                                            <i className="ant-spin-dot-item"></i>
                                            <i className="ant-spin-dot-item"></i>
                                            <i className="ant-spin-dot-item"></i>
                                            <i className="ant-spin-dot-item"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : stores.videoCapture.snapshots.length === 0 ? (
                            <Empty description="暂无截图" />
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {stores.videoCapture.snapshots.map((snapshot) => (
                                    <div 
                                        key={snapshot.id} 
                                        className="relative group p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-all bg-gray-50 dark:bg-gray-900"
                                    >
                                        <img 
                                            src={snapshot.dataUrl} 
                                            alt={`Snapshot ${snapshot.id}`}
                                            className="w-full h-auto rounded"
                                            loading="lazy"
                                        />
                                        <div className="mt-2 flex justify-between items-center">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {new Date(snapshot.timestamp * 1000).toISOString().substr(11, 8)}
                                            </div>
                                            {isSnapshotEdited(snapshot.id) && (
                                                <Tag color="blue" className="ml-1">已编辑</Tag>
                                            )}
                                        </div>
                                        
                                        {/* 操作按钮 */}
                                        <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Tooltip title="下载">
                                                <Button 
                                                    type="primary" 
                                                    size="small" 
                                                    shape="circle"
                                                    icon={<DownloadOutlined />} 
                                                    onClick={() => downloadSnapshot(snapshot)}
                                                />
                                            </Tooltip>
                                            <Tooltip title="编辑">
                                                <Button 
                                                    type="primary" 
                                                    size="small" 
                                                    shape="circle"
                                                    icon={<EditOutlined />} 
                                                    onClick={() => confirmEdit(snapshot)}
                                                />
                                            </Tooltip>
                                            <Tooltip title="删除">
                                                <Button 
                                                    type="primary" 
                                                    danger
                                                    size="small" 
                                                    shape="circle"
                                                    icon={<DeleteOutlined />} 
                                                    onClick={() => deleteSnapshot(snapshot.id)}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* 编辑确认对话框 */}
                    <Modal
                        title="编辑截图"
                        open={editModalVisible}
                        onCancel={() => setEditModalVisible(false)}
                        footer={[
                            <Button key="back" onClick={() => setEditModalVisible(false)}>
                                取消
                            </Button>,
                            <Button
                                key="submit"
                                type="primary"
                                onClick={() => editSnapshot(currentEditSnapshot)}
                            >
                                确定
                            </Button>,
                        ]}
                    >
                        <p>编辑后可在图片编辑器中调整截图效果，支持高分辨率图像处理。编辑完成后可以使用浏览器的返回按钮回到视频截图页面。</p>
                        {currentEditSnapshot && (
                            <div className="text-center mt-4">
                                <img
                                    src={currentEditSnapshot.dataUrl}
                                    alt="待编辑截图"
                                    className="max-w-full max-h-64 mx-auto rounded"
                                />
                            </div>
                        )}
                    </Modal>
                </div>
            </ConfigProvider>
        </StyleProvider>
    );
});

export default VideoCapture; 